import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  listarConferentes,
  aprovarConferente,
  reprovarConferente,
  resetarPinConferente,
  atualizarConferente,
  excluirConferente,
  type ConferenteNovo
} from '../../services/conferente';
import { listarClientes } from '../../services/clientes';
import UserService from '../../services/users';
import { Config, getToken } from '../../services/config';
import ModalPortal from '../../components/Common/ModalPortal';
import ConferenteFormModal from './ConferenteFormModal';

const STATUS_LABELS: Record<string, { label: string; color: string; bg: string }> = {
  pendente: { label: 'Pendente', color: '#d97706', bg: '#fef3c7' },
  aprovado: { label: 'Aprovado', color: '#16a34a', bg: '#dcfce7' },
  reprovado: { label: 'Reprovado', color: '#dc2626', bg: '#fee2e2' }
};

const TIPO_LABELS: Record<string, string> = {
  funcionario: 'Funcionário',
  freelancer: 'Freelancer',
  cooperado: 'Cooperado'
};

const ConferentesList: React.FC = () => {
  const [conferentes, setConferentes] = useState<ConferenteNovo[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Modal de edição
  const [editingConferenteId, setEditingConferenteId] = useState<number | null>(null);

  // Modal de aprovação (com filial + clientes)
  const [aprovandoConferente, setAprovandoConferente] = useState<ConferenteNovo | null>(null);
  const [aprovFilialId, setAprovFilialId] = useState<number | null>(null);
  const [aprovClienteIds, setAprovClienteIds] = useState<number[]>([]);
  const [filiais, setFiliais] = useState<{ id: number; razao_social: string; nome_fantasia?: string; cnpj: string }[]>([]);
  const [clientesDisponiveis, setClientesDisponiveis] = useState<{ id: number; razao_social: string; cnpj?: string }[]>([]);
  const [loadingAprovModal, setLoadingAprovModal] = useState(false);
  const [aprovSaving, setAprovSaving] = useState(false);

  // Link de cadastro
  const [empresaCnpj, setEmpresaCnpj] = useState('');
  const [linkCopiado, setLinkCopiado] = useState(false);

  useEffect(() => {
    axios.get(`${Config.API_BASE_URL}/empresas/perfil`, {
      headers: { Authorization: `Bearer ${getToken()}` }
    }).then(res => {
      if (res.data?.data?.cnpj) setEmpresaCnpj(res.data.data.cnpj.replace(/\D/g, ''));
    }).catch(() => {});
  }, []);

  const linkCadastro = empresaCnpj ? `${window.location.origin}/conferente/${empresaCnpj}` : '';

  const copiarLink = () => {
    if (!linkCadastro) return;
    navigator.clipboard.writeText(linkCadastro).then(() => {
      setLinkCopiado(true);
      setTimeout(() => setLinkCopiado(false), 3000);
    }).catch(() => {});
  };

  const carregarDados = useCallback(async () => {
    try {
      setLoading(true);
      const result = await listarConferentes({
        page,
        limit: 20,
        search: search || undefined,
        status: statusFilter || undefined
      });
      setConferentes(result.conferentes);
      setTotalPages(result.pagination.totalPages);
      setTotal(result.pagination.total);
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Erro ao carregar conferentes' });
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter]);

  useEffect(() => {
    carregarDados();
  }, [carregarDados]);

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // Abrir modal de aprovação (carrega filiais e clientes)
  const handleAbrirAprovacao = async (conf: ConferenteNovo) => {
    setAprovandoConferente(conf);
    setAprovFilialId(null);
    setAprovClienteIds([]);
    setLoadingAprovModal(true);

    try {
      const [filiaisResult, clientesResult] = await Promise.all([
        UserService.getFiliais().catch(() => ({ success: false, filiais: [] as any[] })),
        listarClientes({ limit: 200, ativo: 'true' }).catch(() => ({ success: false, clientes: [] as any[] }))
      ]);

      if (filiaisResult.success && filiaisResult.filiais) {
        setFiliais(filiaisResult.filiais);
      }
      if (clientesResult.success && clientesResult.clientes) {
        setClientesDisponiveis(clientesResult.clientes.map(c => ({
          id: c.id, razao_social: c.razao_social, cnpj: c.cnpj
        })));
      }
    } catch {
      // Continua mesmo sem carregar - os selects ficarão vazios
    } finally {
      setLoadingAprovModal(false);
    }
  };

  const handleConfirmarAprovacao = async () => {
    if (!aprovandoConferente) return;
    setAprovSaving(true);
    try {
      await aprovarConferente(aprovandoConferente.id, {
        filial_id: aprovFilialId || undefined,
        cliente_ids: aprovClienteIds.length > 0 ? aprovClienteIds : undefined
      });
      setMessage({ type: 'success', text: 'Conferente aprovado com sucesso' });
      setAprovandoConferente(null);
      carregarDados();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Erro ao aprovar' });
    } finally {
      setAprovSaving(false);
    }
  };

  const handleReprovar = async (id: number) => {
    if (!window.confirm('Tem certeza que deseja reprovar este conferente?')) return;
    setActionLoading(id);
    try {
      await reprovarConferente(id);
      setMessage({ type: 'success', text: 'Conferente reprovado' });
      carregarDados();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Erro ao reprovar' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleResetPin = async (id: number) => {
    if (!window.confirm('Tem certeza que deseja resetar o PIN deste conferente? O PIN será definido como os 4 últimos dígitos do CPF.')) return;
    setActionLoading(id);
    try {
      await resetarPinConferente(id);
      setMessage({ type: 'success', text: 'PIN resetado com sucesso. O conferente deverá usar os 4 últimos dígitos do CPF como PIN temporário.' });
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Erro ao resetar PIN' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleAtivo = async (conf: ConferenteNovo) => {
    const novoStatus = !conf.ativo;
    const acao = novoStatus ? 'reativar' : 'desativar';
    if (!window.confirm(`Tem certeza que deseja ${acao} o conferente "${conf.nome}"?`)) return;
    setActionLoading(conf.id);
    try {
      await atualizarConferente(conf.id, { ativo: novoStatus });
      setMessage({ type: 'success', text: `Conferente ${novoStatus ? 'reativado' : 'desativado'} com sucesso` });
      carregarDados();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || `Erro ao ${acao}` });
    } finally {
      setActionLoading(null);
    }
  };

  const handleExcluir = async (conf: ConferenteNovo) => {
    if (!window.confirm(`Tem certeza que deseja EXCLUIR permanentemente o conferente "${conf.nome}"? Esta ação não pode ser desfeita.`)) return;
    setActionLoading(conf.id);
    try {
      const result = await excluirConferente(conf.id);
      setMessage({ type: 'success', text: result.message || 'Conferente excluído com sucesso' });
      carregarDados();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Erro ao excluir conferente' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleAprovClienteToggle = (clienteId: number) => {
    setAprovClienteIds(prev =>
      prev.includes(clienteId)
        ? prev.filter(id => id !== clienteId)
        : [...prev, clienteId]
    );
  };

  const formatCpf = (cpf: string) => {
    if (!cpf || cpf.length !== 11) return cpf;
    return `${cpf.slice(0, 3)}.${cpf.slice(3, 6)}.${cpf.slice(6, 9)}-${cpf.slice(9)}`;
  };

  const pendentesCount = conferentes.filter(c => c.status === 'pendente').length;

  const btnStyle = (bg: string, color: string, border?: string): React.CSSProperties => ({
    padding: '4px 10px', borderRadius: '6px',
    border: border || 'none', background: bg, color,
    fontSize: '0.8rem', cursor: 'pointer', fontWeight: 500
  });

  return (
    <div className="page-content">
      <div className="page-header">
        <div>
          <h1 className="page-title">Conferentes</h1>
          <p className="page-subtitle">
            Gerenciamento de conferentes cadastrados - {total} conferente{total !== 1 ? 's' : ''}
            {pendentesCount > 0 && (
              <span style={{ color: '#d97706', fontWeight: 600 }}>
                {' '}({pendentesCount} pendente{pendentesCount > 1 ? 's' : ''})
              </span>
            )}
          </p>
        </div>
      </div>

      {message && (
        <div style={{
          padding: '12px 16px', borderRadius: '8px', marginBottom: '16px',
          background: message.type === 'success' ? '#dcfce7' : '#fee2e2',
          color: message.type === 'success' ? '#16a34a' : '#dc2626',
          border: `1px solid ${message.type === 'success' ? '#86efac' : '#fca5a5'}`,
          fontSize: '0.9rem'
        }}>
          {message.text}
        </div>
      )}

      {/* Link de cadastro para conferentes */}
      {linkCadastro && (
        <div style={{
          padding: '16px 20px', borderRadius: '10px', marginBottom: '20px',
          background: '#f0fdf4', border: '1px solid #86efac',
          display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap'
        }}>
          <i className="fas fa-qrcode" style={{ fontSize: '1.4rem', color: '#16a34a' }} />
          <div style={{ flex: '1 1 300px' }}>
            <p style={{ margin: 0, fontSize: '0.85rem', fontWeight: 600, color: '#15803d' }}>
              Link de cadastro para conferentes
            </p>
            <p style={{ margin: '2px 0 0', fontSize: '0.8rem', color: '#16a34a', wordBreak: 'break-all' }}>
              {linkCadastro}
            </p>
          </div>
          <button
            onClick={copiarLink}
            style={{
              padding: '8px 16px', borderRadius: '8px', border: 'none',
              background: linkCopiado ? '#16a34a' : '#15803d', color: '#fff',
              fontSize: '0.85rem', cursor: 'pointer', fontWeight: 500,
              display: 'flex', alignItems: 'center', gap: '6px',
              whiteSpace: 'nowrap', transition: 'background 0.2s'
            }}
          >
            <i className={linkCopiado ? 'fas fa-check' : 'fas fa-copy'} />
            {linkCopiado ? 'Copiado!' : 'Copiar link'}
          </button>
        </div>
      )}

      {/* Filtros */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Buscar por nome ou CPF..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          style={{
            flex: '1 1 250px', padding: '8px 12px',
            border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.9rem'
          }}
        />
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          style={{
            padding: '8px 12px', border: '1px solid #d1d5db',
            borderRadius: '8px', fontSize: '0.9rem', background: '#fff'
          }}
        >
          <option value="">Todos os status</option>
          <option value="pendente">Pendentes</option>
          <option value="aprovado">Aprovados</option>
          <option value="reprovado">Reprovados</option>
        </select>
      </div>

      {/* Tabela */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: '#64748b' }}>Carregando...</div>
      ) : conferentes.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: '#64748b' }}>
          Nenhum conferente encontrado.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Nome', 'CPF', 'Tipo', 'Status', 'Ativo', 'Filial', 'Telefone', 'Cadastro', 'Acoes'].map((h, i) => (
                  <th key={h} style={{
                    textAlign: i === 3 || i === 4 || i === 8 ? 'center' : 'left',
                    padding: '12px 16px', borderBottom: '2px solid #e2e8f0',
                    fontSize: '0.85rem', color: '#64748b', fontWeight: 600
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {conferentes.map((conf) => {
                const statusInfo = STATUS_LABELS[conf.status] || STATUS_LABELS.pendente;
                const isDisabled = actionLoading === conf.id;
                return (
                  <tr key={conf.id} style={{
                    borderBottom: '1px solid #f1f5f9',
                    opacity: conf.ativo ? 1 : 0.6,
                    background: conf.ativo ? 'transparent' : '#f8fafc'
                  }}>
                    <td style={{ padding: '12px 16px', fontWeight: 500 }}>{conf.nome}</td>
                    <td style={{ padding: '12px 16px', fontFamily: 'monospace', fontSize: '0.85rem' }}>{formatCpf(conf.cpf)}</td>
                    <td style={{ padding: '12px 16px', fontSize: '0.85rem' }}>{TIPO_LABELS[conf.tipo_conferente] || conf.tipo_conferente}</td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      <span style={{
                        padding: '4px 10px', borderRadius: '12px', fontSize: '0.8rem', fontWeight: 600,
                        color: statusInfo.color, background: statusInfo.bg
                      }}>
                        {statusInfo.label}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      <span style={{
                        padding: '4px 10px', borderRadius: '12px', fontSize: '0.8rem', fontWeight: 600,
                        color: conf.ativo ? '#16a34a' : '#dc2626',
                        background: conf.ativo ? '#dcfce7' : '#fee2e2'
                      }}>
                        {conf.ativo ? 'Sim' : 'Não'}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: '0.85rem' }}>{conf.filial_nome || '-'}</td>
                    <td style={{ padding: '12px 16px', fontSize: '0.85rem' }}>{conf.telefone || '-'}</td>
                    <td style={{ padding: '12px 16px', fontSize: '0.85rem', color: '#64748b' }}>
                      {conf.criado_em ? new Date(conf.criado_em).toLocaleDateString('pt-BR') : '-'}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', flexWrap: 'wrap' }}>
                        {conf.status === 'pendente' && (
                          <>
                            <button
                              onClick={() => handleAbrirAprovacao(conf)}
                              disabled={isDisabled}
                              style={{ ...btnStyle('#16a34a', '#fff'), opacity: isDisabled ? 0.5 : 1 }}
                            >
                              Aprovar
                            </button>
                            <button
                              onClick={() => handleReprovar(conf.id)}
                              disabled={isDisabled}
                              style={{ ...btnStyle('#fff', '#dc2626', '1px solid #dc2626'), opacity: isDisabled ? 0.5 : 1 }}
                            >
                              Reprovar
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => setEditingConferenteId(conf.id)}
                          disabled={isDisabled}
                          title="Editar conferente"
                          style={{ ...btnStyle('#3b82f6', '#fff'), opacity: isDisabled ? 0.5 : 1 }}
                        >
                          Editar
                        </button>
                        <button
                          onClick={() => handleToggleAtivo(conf)}
                          disabled={isDisabled}
                          title={conf.ativo ? 'Desativar conferente' : 'Reativar conferente'}
                          style={{
                            ...btnStyle(
                              conf.ativo ? '#fff' : '#16a34a',
                              conf.ativo ? '#d97706' : '#fff',
                              conf.ativo ? '1px solid #d97706' : undefined
                            ),
                            opacity: isDisabled ? 0.5 : 1
                          }}
                        >
                          {conf.ativo ? 'Desativar' : 'Reativar'}
                        </button>
                        <button
                          onClick={() => handleResetPin(conf.id)}
                          disabled={isDisabled}
                          title="Resetar PIN"
                          style={{ ...btnStyle('#fff', '#64748b', '1px solid #d1d5db'), opacity: isDisabled ? 0.5 : 1 }}
                        >
                          Reset PIN
                        </button>
                        <button
                          onClick={() => handleExcluir(conf)}
                          disabled={isDisabled}
                          title="Excluir conferente permanentemente"
                          style={{ ...btnStyle('#dc2626', '#fff'), opacity: isDisabled ? 0.5 : 1 }}
                        >
                          Excluir
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Paginacao */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '20px' }}>
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            style={{
              padding: '6px 12px', borderRadius: '6px', border: '1px solid #d1d5db',
              background: '#fff', cursor: page === 1 ? 'not-allowed' : 'pointer',
              opacity: page === 1 ? 0.5 : 1, fontSize: '0.85rem'
            }}
          >
            Anterior
          </button>
          <span style={{ padding: '6px 12px', fontSize: '0.85rem', color: '#64748b' }}>
            Pagina {page} de {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            style={{
              padding: '6px 12px', borderRadius: '6px', border: '1px solid #d1d5db',
              background: '#fff', cursor: page === totalPages ? 'not-allowed' : 'pointer',
              opacity: page === totalPages ? 0.5 : 1, fontSize: '0.85rem'
            }}
          >
            Proximo
          </button>
        </div>
      )}

      {/* Modal de Edição */}
      {editingConferenteId && (
        <ConferenteFormModal
          conferenteId={editingConferenteId}
          onClose={() => setEditingConferenteId(null)}
          onSuccess={(msg) => {
            setEditingConferenteId(null);
            setMessage({ type: 'success', text: msg });
            carregarDados();
          }}
        />
      )}

      {/* Modal de Aprovação com Filial + Clientes */}
      {aprovandoConferente && (
        <ModalPortal isOpen={true}>
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10001
          }} onClick={() => setAprovandoConferente(null)}>
            <div onClick={e => e.stopPropagation()} style={{
              background: '#fff', borderRadius: 12, width: '100%', maxWidth: 550,
              maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}>
              {/* Header */}
              <div style={{
                padding: '20px 24px', borderBottom: '1px solid #e2e8f0',
                background: '#f0fdf4'
              }}>
                <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600, color: '#16a34a' }}>
                  Aprovar Conferente
                </h2>
                <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: '#64748b' }}>
                  {aprovandoConferente.nome} - CPF: {formatCpf(aprovandoConferente.cpf)}
                </p>
              </div>

              <div style={{ padding: '20px 24px' }}>
                {loadingAprovModal ? (
                  <div style={{ textAlign: 'center', padding: 20, color: '#64748b' }}>Carregando dados...</div>
                ) : (
                  <>
                    <p style={{ margin: '0 0 16px', fontSize: '0.9rem', color: '#374151' }}>
                      Selecione a filial e os clientes que este conferente tera acesso para realizar conferencias:
                    </p>

                    {/* Filial */}
                    <div style={{ marginBottom: 16 }}>
                      <label style={{ display: 'block', marginBottom: 4, fontSize: '0.85rem', fontWeight: 500, color: '#374151' }}>
                        Filial
                      </label>
                      {filiais.length === 0 ? (
                        <div style={{ padding: '8px 0', color: '#7f8c8d', fontSize: '0.85rem' }}>
                          Nenhuma filial cadastrada
                        </div>
                      ) : (
                        <select
                          value={aprovFilialId || ''}
                          onChange={e => setAprovFilialId(e.target.value ? parseInt(e.target.value) : null)}
                          style={{
                            width: '100%', padding: '8px 12px', border: '1px solid #d1d5db',
                            borderRadius: 8, fontSize: '0.9rem', background: '#fff', boxSizing: 'border-box'
                          }}
                        >
                          <option value="">Selecione uma filial</option>
                          {filiais.map(f => (
                            <option key={f.id} value={f.id}>
                              {f.nome_fantasia || f.razao_social} ({f.cnpj})
                            </option>
                          ))}
                        </select>
                      )}
                      <small style={{ display: 'block', marginTop: 4, color: '#7f8c8d', fontSize: '0.75rem' }}>
                        Filial onde este conferente esta alocado
                      </small>
                    </div>

                    {/* Clientes */}
                    <div>
                      <label style={{ display: 'block', marginBottom: 4, fontSize: '0.85rem', fontWeight: 500, color: '#374151' }}>
                        Clientes Vinculados
                      </label>
                      {clientesDisponiveis.length === 0 ? (
                        <div style={{ padding: '8px 0', color: '#7f8c8d', fontSize: '0.85rem' }}>
                          Nenhum cliente cadastrado
                        </div>
                      ) : (
                        <div style={{
                          maxHeight: 200, overflowY: 'auto', border: '1px solid #ddd',
                          borderRadius: 8, padding: 8, background: '#fff'
                        }}>
                          {clientesDisponiveis.map(cliente => (
                            <label
                              key={cliente.id}
                              style={{
                                display: 'flex', alignItems: 'center', padding: '6px 8px',
                                borderRadius: 6, cursor: 'pointer',
                                backgroundColor: aprovClienteIds.includes(cliente.id) ? '#e8f5e9' : 'transparent',
                                transition: 'background-color 0.2s'
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={aprovClienteIds.includes(cliente.id)}
                                onChange={() => handleAprovClienteToggle(cliente.id)}
                                style={{ marginRight: 8 }}
                              />
                              <span style={{ flex: 1 }}>
                                <strong style={{ fontSize: '0.85rem' }}>{cliente.razao_social}</strong>
                                {cliente.cnpj && (
                                  <small style={{ display: 'block', color: '#7f8c8d', fontSize: '0.72rem' }}>
                                    CNPJ: {cliente.cnpj}
                                  </small>
                                )}
                              </span>
                            </label>
                          ))}
                        </div>
                      )}
                      <small style={{ display: 'block', marginTop: 4, color: '#7f8c8d', fontSize: '0.75rem' }}>
                        {aprovClienteIds.length === 0
                          ? 'Selecione os clientes. Se nenhum for selecionado, todos serao exibidos.'
                          : `${aprovClienteIds.length} cliente${aprovClienteIds.length > 1 ? 's' : ''} selecionado${aprovClienteIds.length > 1 ? 's' : ''}`
                        }
                      </small>
                    </div>
                  </>
                )}
              </div>

              {/* Footer */}
              <div style={{
                padding: '16px 24px', borderTop: '1px solid #e2e8f0',
                display: 'flex', justifyContent: 'flex-end', gap: 8
              }}>
                <button
                  onClick={() => setAprovandoConferente(null)}
                  disabled={aprovSaving}
                  style={{
                    padding: '8px 16px', borderRadius: 8, border: '1px solid #d1d5db',
                    background: '#fff', color: '#374151', fontSize: '0.9rem', cursor: 'pointer', fontWeight: 500
                  }}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleConfirmarAprovacao}
                  disabled={aprovSaving || loadingAprovModal}
                  style={{
                    padding: '8px 16px', borderRadius: 8, border: 'none',
                    background: '#16a34a', color: '#fff', fontSize: '0.9rem', cursor: 'pointer',
                    fontWeight: 500, opacity: aprovSaving ? 0.6 : 1
                  }}
                >
                  {aprovSaving ? 'Aprovando...' : 'Confirmar Aprovacao'}
                </button>
              </div>
            </div>
          </div>
        </ModalPortal>
      )}
    </div>
  );
};

export default ConferentesList;
